import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Alert, Button, Card } from "@platform/design-system";
import { useState } from "react";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { downloadCaseDocument } from "../api/caseDocuments";

import { useCaseDocuments } from "./caseQueries";
import { SectionStatus } from "./SectionStatus";

import type { ReactNode } from "react";

export const CASE_DOCUMENTS_TEST_ID = "golden-case-documents";

/** The broker and proposal documents imported for the case, each downloadable. */
export const CaseDocuments = ({
  caseId,
}: {
  readonly caseId: number;
}): ReactNode => {
  const client = useGoldenClient();
  const documents = useCaseDocuments(caseId);
  const [downloadError, setDownloadError] = useState<string>();

  const download = async (id: number, fileName: string): Promise<void> => {
    setDownloadError(undefined);
    try {
      await downloadCaseDocument(client, caseId, { id, fileName });
    } catch (error: unknown) {
      setDownloadError(
        error instanceof Error ? error.message : "The download failed",
      );
    }
  };

  return (
    <Card data-testid={CASE_DOCUMENTS_TEST_ID} title="Documents">
      {downloadError && (
        <Alert
          data-testid={`${CASE_DOCUMENTS_TEST_ID}-download-error`}
          severity="error"
        >
          {downloadError}
        </Alert>
      )}
      {documents.isSuccess ? (
        <Table size="small" aria-label="Documents">
          <TableHead>
            <TableRow>
              <TableCell>Kind</TableCell>
              <TableCell>File</TableCell>
              <TableCell>SharePoint path</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.data.map((document) => (
              <TableRow
                key={document.id}
                data-testid={`${CASE_DOCUMENTS_TEST_ID}-row-${document.id}`}
              >
                <TableCell>{document.kind}</TableCell>
                <TableCell>{document.file_name}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {document.sharepoint_path}
                </TableCell>
                <TableCell>
                  <Button
                    variant="tertiary"
                    size="sm"
                    data-testid={`${CASE_DOCUMENTS_TEST_ID}-download-${document.id}`}
                    onClick={() =>
                      void download(document.id, document.file_name)
                    }
                  >
                    Download
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {documents.data.length === 0 && (
              <TableRow data-testid={`${CASE_DOCUMENTS_TEST_ID}-empty`}>
                <TableCell colSpan={4}>No documents imported.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      ) : (
        <SectionStatus
          testId={CASE_DOCUMENTS_TEST_ID}
          label="documents"
          error={documents.error}
        />
      )}
    </Card>
  );
};
